// Utility functions to transform API data into chart-ready format

/**
 * Transform task analytics data into grouped bar chart format
 * Shows task status (completed/in-progress/pending) over the last 7 days
 */
export const transformTaskStatusData = (scans, tasks) => {
  if (!scans || scans.length === 0) {
    return [];
  }

  const last7Days = getLast7Days();
  const taskStatusByDate = {};

  // Initialize data structure
  last7Days.forEach(date => {
    taskStatusByDate[date] = {
      date: formatDateLabel(date),
      completed: 0,
      inProgress: 0,
      pending: 0,
    };
  });

  // Count task status changes from daily scans (exclude on_leave days)
  scans.forEach(scan => {
    // Skip scans marked as on_leave
    if (scan.on_leave) {
      return;
    }
    
    const scanDate = scan.date;
    if (taskStatusByDate[scanDate]) {
      const taskUpdates = scan.dimensions?.cness_tasks?.task_updates || [];
      taskUpdates.forEach(update => {
        if (update.status === 'completed') {
          taskStatusByDate[scanDate].completed++;
        } else if (update.status === 'in-progress') {
          taskStatusByDate[scanDate].inProgress++;
        } else if (update.status === 'pending') {
          taskStatusByDate[scanDate].pending++;
        }
      });
    }
  });

  const result = Object.values(taskStatusByDate);
  console.log('transformTaskStatusData result:', result);
  return result;
};

/**
 * Transform daily scan data into horizontal bar chart format
 * Shows average hours per dimension
 */
export const transformAverageHoursData = (analytics) => {
  if (!analytics || !analytics.average_hours) {
    console.log('transformAverageHoursData: No analytics data');
    return [];
  }

  const dimensionLabels = {
    cness_tasks: 'CNESS Tasks',
    self_care: 'Self Care',
    rnd_learning: 'R&D Learning',
    social_media: 'Social Media',
    personal_time: 'Personal Time',
    inspired_work: 'Inspired Work',
  };

  const result = Object.entries(analytics.average_hours)
    .map(([key, hours]) => ({
      name: dimensionLabels[key] || key,
      hours: parseFloat(hours.toFixed(2)),
    }))
    .sort((a, b) => b.hours - a.hours); // Sort by hours descending
  
  console.log('transformAverageHoursData result:', result);
  return result;
};

/**
 * Transform mood distribution data into colored bar chart format
 */
export const transformMoodData = (analytics) => {
  if (!analytics || !analytics.mood_distribution) {
    console.log('transformMoodData: No analytics data');
    return [];
  }

  const moodLabels = {
    very_happy: 'Very Happy',
    happy: 'Happy',
    neutral: 'Neutral',
    sad: 'Sad',
    very_sad: 'Very Sad',
  };

  const moodOrder = ['very_happy', 'happy', 'neutral', 'sad', 'very_sad'];

  const result = moodOrder
    .filter(key => analytics.mood_distribution[key])
    .map(key => ({
      name: moodLabels[key],
      value: analytics.mood_distribution[key],
    }));
  
  console.log('transformMoodData result:', result);
  return result;
};

/**
 * Get array of last 7 days in YYYY-MM-DD format
 */
const getLast7Days = () => {
  const days = [];
  const today = new Date();
  
  for (let i = 6; i >= 0; i--) {
    const date = new Date(today);
    date.setDate(today.getDate() - i);
    days.push(date.toISOString().split('T')[0]);
  }
  
  return days;
};

/**
 * Format date for chart labels (e.g., "Mon 10/16")
 */
const formatDateLabel = (dateString) => {
  const date = new Date(dateString);
  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];
  const dayName = dayNames[date.getDay()];
  const month = date.getMonth() + 1;
  const day = date.getDate();
  
  return `${dayName} ${month}/${day}`;
};
